import { useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { mdiPlus } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useCan } from '../../contexts/PermissionsContext'
import {
    useAssetKnowledge,
    useCreateAssetNote,
    useDeleteAssetNote,
    useUpdateAssetNote,
} from '../../hooks/useKnowledge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { InheritedKnowledge } from './InheritedKnowledge'
import { NoteCard } from './NoteCard'
import { NoteForm } from './NoteForm'

/**
 * The Notes section of an asset (spec 2026-10-10-06): the asset's own titled
 * notes, editable by whoever holds `knowledge.write`, and below them the notes
 * inherited from its ancestors and the organization, read-only.
 *
 * One editor is open at a time: the editor's Ctrl+S is global to the page.
 */
export function AssetNotesSection({ org, assetUid }: { org: string; assetUid?: string }) {
    const { t } = useTranslation('knowledge')
    const { t: tAssets } = useTranslation('assets')
    const canWrite = useCan('knowledge.write')
    const { note: highlightedNote } = useSearch({ strict: false }) as { note?: string }
    const { data: notes, isLoading, error } = useAssetKnowledge(org, assetUid)
    const [editing, setEditing] = useState<string | 'new' | null>(null)
    const create = useCreateAssetNote(org, assetUid ?? '')
    const update = useUpdateAssetNote(org, assetUid ?? '')
    const remove = useDeleteAssetNote(org, assetUid ?? '')

    const own = (notes ?? []).filter((n) => !n.inherited)
    const inherited = (notes ?? []).filter((n) => n.inherited)

    return (
        <div data-testid="right-panel-section-notes" className="rounded-lg border border-border bg-card p-6">
            <div className="mb-4 flex items-start justify-between gap-2">
                <h3 className="text-lg font-semibold text-foreground">{tAssets('detail.notes')}</h3>
                {canWrite && editing === null && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing('new')}
                        aria-label={t('add_note')}
                        title={t('add_note')}
                        data-testid="add-note-btn"
                    >
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('add_note')}</span>
                    </Button>
                )}
            </div>

            {isLoading ? (
                <p className="py-4 text-sm text-muted-foreground">{t('loading')}</p>
            ) : error ? (
                <p className="text-destructive" role="alert">{t('load_failed')}</p>
            ) : (
                <>
                    {editing === 'new' && (
                        <div className="mb-3">
                            <NoteForm
                                withSubtreeOption
                                testIdPrefix="new-note"
                                onCancel={() => setEditing(null)}
                                onSubmit={async (input) => {
                                    await create.mutateAsync(input)
                                    setEditing(null)
                                }}
                            />
                        </div>
                    )}
                    {own.length === 0 && editing !== 'new' ? (
                        <EmptyState
                            variant="inline"
                            title={t('empty_asset')}
                            data-testid="asset-notes-empty"
                        />
                    ) : (
                        <ul className="space-y-2" data-testid="asset-notes-list">
                            {own.map((note) =>
                                editing === note.uid ? (
                                    <li key={note.uid}>
                                        <NoteForm
                                            note={note}
                                            withSubtreeOption
                                            testIdPrefix="edit-note"
                                            onCancel={() => setEditing(null)}
                                            onSubmit={async (input) => {
                                                await update.mutateAsync({ noteUid: note.uid, ...input })
                                                setEditing(null)
                                            }}
                                        />
                                    </li>
                                ) : (
                                    <NoteCard
                                        key={note.uid}
                                        org={org}
                                        note={note}
                                        canWrite={canWrite}
                                        historyAssetUid={assetUid}
                                        highlighted={note.uid === highlightedNote}
                                        onEdit={() => setEditing(note.uid)}
                                        onDelete={() => remove.mutate(note.uid)}
                                        deleting={remove.isPending}
                                    />
                                ),
                            )}
                        </ul>
                    )}
                    <InheritedKnowledge org={org} notes={inherited} />
                </>
            )}
        </div>
    )
}
